/**
 * Clarity One My Projects — customer-facing project status (exactly four tabs).
 * Do not invent additional lifecycle states.
 */

export const CLARITY_ONE_PROJECT_TABS = [
  "pending_approval",
  "active",
  "completed",
  "cancelled",
] as const;

export type ClarityOneProjectTab = (typeof CLARITY_ONE_PROJECT_TABS)[number];

export type ClarityOneProjectStatusLabel =
  | "Pending Approval"
  | "Active"
  | "Completed"
  | "Cancelled";

export const CLARITY_ONE_PROJECT_STATUS_LABEL: Record<
  ClarityOneProjectTab,
  ClarityOneProjectStatusLabel
> = {
  pending_approval: "Pending Approval",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
};

export type ClarityOneProjectClassifyInput = {
  /** From getProposalPublicAccessState().state */
  accessState: string;
  pipelineStage?: string | null;
  cancelledAt?: Date | string | null;
  /** True when at least one VO is status "sent" for this proposal. */
  hasPendingVariationOrder: boolean;
};

/**
 * Exclusive tab assignment (priority):
 * 1. Cancelled (accepted+cancelled banner, or cancelled unaccepted)
 * 2. Pending Approval (offer awaiting acceptance, or accepted with VO awaiting approval)
 * 3. Completed
 * 4. Active
 *
 * Returns null when the record must not appear (draft, expired, archived-unaccepted, etc.).
 */
export function classifyClarityOneProjectTab(
  input: ClarityOneProjectClassifyInput,
): ClarityOneProjectTab | null {
  const state = String(input.accessState ?? "");
  const cancelled =
    input.cancelledAt != null && String(input.cancelledAt).trim() !== "";
  const stage = String(input.pipelineStage ?? "").toLowerCase();

  if (state === "CANCELLED_UNACCEPTED") return "cancelled";

  if (state === "PERMANENT_ACCEPTED_RECORD") {
    if (cancelled) return "cancelled";
    if (input.hasPendingVariationOrder) return "pending_approval";
    if (stage === "completed") return "completed";
    return "active";
  }

  if (state === "ACTIVE_OFFER") return "pending_approval";

  // ARCHIVED_INVALID, EXPIRED, NOT_PUBLIC — not customer project list items
  return null;
}

export type ClarityOneProjectHistoryKind =
  | "proposal_accepted"
  | "proposal_issued"
  | "vo_sent"
  | "vo_approved"
  | "vo_rejected";

export function clarityOneProjectHistoryTitle(kind: ClarityOneProjectHistoryKind, voNumber?: string): string {
  const raw = String(voNumber ?? "").trim();
  const voLabel = raw
    ? raw.toUpperCase().startsWith("VO")
      ? raw
      : `#${raw}`
    : "";
  const voSuffix = voLabel ? ` ${voLabel}` : "";
  switch (kind) {
    case "proposal_accepted":
      return "Proposal Accepted";
    case "proposal_issued":
      return "Proposal Issued";
    case "vo_sent":
      return `Variation Order${voSuffix} Awaiting Approval`;
    case "vo_approved":
      return `Variation Order${voSuffix} Approved`;
    case "vo_rejected":
      return `Variation Order${voSuffix} Declined`;
  }
}
